import React, { useMemo, useRef, useState } from 'react';
import TextField from '@shared/ui/atoms/TextField';
import { Switch } from '@shared/ui/atoms/Switch';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  selectExistingMeetingRoomBooking,
  selectIsSavingMeetingBooking,
  selectIsTeamsMeeting,
  selectMeetingBodyText,
  selectMeetingFromTime,
  selectMeetingName,
  selectMeetingRoom,
  selectMeetingToTime,
  setIsTeamsMeeting,
  setMeetingBodyText,
  setMeetingFromTime,
  setMeetingName,
  setMeetingToTime
} from "../../models/meeting-room-booking-dialog.duck";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { useTranslation } from "react-i18next";
import Colours from '@shared/ui/atoms/Colours';
import { Time, TimeRangeSelectorV2 } from "../../components/TimeRangeSelectorV2";
import { MeetingRecurrenceSection } from "./MeetingRecurrenceSection";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function MeetingRoomBookingTabBookingView(props: Props) {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const meetingName = useSelector(selectMeetingName);
  const isTeamsMeeting = useSelector(selectIsTeamsMeeting);
  const fromTime = useSelector(selectMeetingFromTime);
  const toTime = useSelector(selectMeetingToTime);
  const meetingRoom = useSelector(selectMeetingRoom);
  const isSavingMeetingRoomBooking = useSelector(selectIsSavingMeetingBooking);
  const meetingBodyText = useSelector(selectMeetingBodyText);
  const existingMeetingRoomBooking = useSelector(selectExistingMeetingRoomBooking);
  const textRef = useRef<any>(null);
  const [showMore, setShowMore] = useState(!!meetingBodyText);

  const isOutlookRoom = useMemo(() => {
    return !!meetingRoom?.microsoftRoomId;
  }, [meetingRoom]);

  const isBookingFromOutlook = useMemo(() => {
    return !!existingMeetingRoomBooking?.outlookEventUid;
  }, [existingMeetingRoomBooking?.outlookEventUid]);

  const canUpdateMeetingRoomFields = useMemo(() => {
    if (existingMeetingRoomBooking?.teamTodayMeetingId) {
      if (isBookingFromOutlook) {
        return false;
      }
    }
    return true;
  }, [existingMeetingRoomBooking, isBookingFromOutlook]);

  const isOutlookBookedMeetingWithNoTeamTodayMeeting = useMemo(() => {
    return !existingMeetingRoomBooking?.teamTodayMeetingId && isBookingFromOutlook;
  }, [existingMeetingRoomBooking?.teamTodayMeetingId, isBookingFromOutlook]);

  const onMeetingDescriptionChanged = (text: string) => {
    if (props.userCanSaveBooking) {
      dispatch(setMeetingBodyText(text));
    }
  }

  const onTimeChange = (from: Time, to?: Time) => {
    if (!to) return
    if (toTime && fromTime) {
      const updatedToTime = toTime.clone().minutes(to.minutes).hour(to.hour);
      dispatch(setMeetingToTime(updatedToTime));

      const updatedFromTime = fromTime.clone().minutes(from.minutes).hour(from.hour);
      dispatch(setMeetingFromTime(updatedFromTime));
    }
  }

  return (
    <>
      {isOutlookBookedMeetingWithNoTeamTodayMeeting && <BodyRegular style={{marginBottom: 16}}>{t('room-booking-dialog.outlook-booked-meeting')}</BodyRegular>}
      <Fields>
        <TextField onChange={(name: string) => dispatch(setMeetingName(name))}
                   label={'room-booking-dialog.meeting-title'}
                   disabled={!canUpdateMeetingRoomFields || isSavingMeetingRoomBooking || !props.userCanSaveBooking}
                   value={meetingName} />
        <TimeRangeSelectorV2 style={{marginBottom: 24}}
                             fromTime={{hour: fromTime?.hour() ?? 0, minutes: fromTime?.minutes() ?? 0}}
                             toTime={{hour: toTime?.hour() ?? 0, minutes: toTime?.minutes() ?? 0}}
                             onTimeChange={onTimeChange}
                             disabled={isSavingMeetingRoomBooking} />
        {isOutlookRoom && <Switch value={!!isTeamsMeeting} label={'room-booking-dialog.is-teams-meeting'}
                                  onChange={(val: boolean) => dispatch(setIsTeamsMeeting(val))}
                                  style={{marginBottom: 16}}
                                  disabled={!isOutlookRoom || isSavingMeetingRoomBooking || !props.userCanSaveBooking} />}
        <OutlineButton text={showMore ? 'button.show-less' : 'button.show-more'} click={() => setShowMore(!showMore)} size={ButtonSize.Small} style={{maxWidth: 110, marginBottom: 16}} />
        {showMore && <>
          {isOutlookRoom && <MeetingRecurrenceSection />}
          <BodySmall colour={Colours.darkGrey} weight={600} style={{marginBottom: 4}}>{t('room-booking-dialog.notes')}</BodySmall>
          <TextArea ref={textRef} defaultValue={''}
                    disabled={!!existingMeetingRoomBooking}
                    value={meetingBodyText || ''}
                    onChange={(e) => onMeetingDescriptionChanged(e.target.value)} />
        </>}
      </Fields>
    </>
  )
}

interface Props {
  userCanSaveBooking: boolean;
}

const Fields = styled.div`
  width: 400px;
  max-width: 100%;
  margin-bottom: 52px;
  .switch__label {
    font-weight: 600;
    color: ${Colours.darkGrey};
    margin-right: 24px;
  }
  .switch {
    justify-content: flex-start;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  margin-bottom: 16px;
  padding: 4px 8px;
  font-size: 16px;
  font-family: "soleil", sans-serif;
  white-space: pre-wrap;
`
